import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const getActionBadge = (actionType) => {
  switch (actionType) {
    case 'confirmed_warrior':
      return {
        label: 'Confirmed Warrior',
        icon: <CheckCircleIcon sx={{ fontSize: 15 }} />,
        color: '#10B981',
        bg: 'rgba(16, 185, 129, 0.12)',
        border: 'rgba(16, 185, 129, 0.3)'
      };
    case 'requested':
      return {
        label: 'Requested Prayer',
        icon: <CampaignIcon sx={{ fontSize: 15 }} />,
        color: '#7C3AED',
        bg: 'rgba(124, 58, 237, 0.12)',
        border: 'rgba(124, 58, 237, 0.3)'
      };
    case 'commented':
      return {
        label: 'Commented',
        icon: <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />,
        color: '#0D9488',
        bg: 'rgba(13, 148, 136, 0.12)',
        border: 'rgba(13, 148, 136, 0.3)'
      };
    case 'opened':
      return {
        label: 'Opened',
        icon: <VisibilityIcon sx={{ fontSize: 15 }} />,
        color: '#0288D1',
        bg: 'rgba(2, 136, 209, 0.12)',
        border: 'rgba(2, 136, 209, 0.3)'
      };
    case 'reacted':
      return {
        label: 'Reacted',
        icon: <FavoriteIcon sx={{ fontSize: 15 }} />,
        color: '#E91E63',
        bg: 'rgba(233, 30, 99, 0.12)',
        border: 'rgba(233, 30, 99, 0.3)'
      };
    case 'prayed':
      return {
        label: 'Prayed',
        icon: <VolunteerActivismIcon sx={{ fontSize: 15 }} />,
        color: '#4CAF50',
        bg: 'rgba(76, 175, 80, 0.12)',
        border: 'rgba(76, 175, 80, 0.3)'
      };
    case 'subscribed':
      return {
        label: 'Subscribed',
        icon: <NotificationsActiveIcon sx={{ fontSize: 15 }} />,
        color: '#FF9800',
        bg: 'rgba(255, 152, 0, 0.12)',
        border: 'rgba(255, 152, 0, 0.3)'
      };
    case 'unsubscribed':
      return {
        label: 'Unsubscribed',
        icon: <CloseIcon sx={{ fontSize: 15 }} />,
        color: '#EF4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: 'rgba(239, 68, 68, 0.3)'
      };
    default:
      return {
        label: actionType || 'Activity',
        icon: <TimelineIcon sx={{ fontSize: 15 }} />,
        color: '#9E9E9E',
        bg: 'rgba(158, 158, 158, 0.12)',
        border: 'rgba(158, 158, 158, 0.3)'
      };
  }
};

const formatTimeSlotDisplay = (slotObj, subObj) => {
  if (!slotObj && !subObj) return '';
  const slot = (typeof slotObj === 'object' && slotObj !== null) ? slotObj : {};
  const sub = (typeof subObj === 'object' && subObj !== null) ? subObj : {};
  const from = slot.from_slot || slot.start_time || slot.from || sub.from_slot || sub.start_time;
  const to = slot.to_slot || slot.end_time || slot.to || sub.to_slot || sub.end_time;
  if (from && to && from !== 'undefined' && to !== 'undefined') {
    return `${from} - ${to}`;
  }
  if (from && from !== 'undefined') return from;
  if (to && to !== 'undefined') return to;
  if (typeof slotObj === 'string' && slotObj !== 'undefined' && slotObj.trim()) return slotObj.trim();
  return '';
};

const getSubscriberLastActivity = (s) => {
  const dates = [
    s.updated_at,
    s.app_users?.last_used,
    s.app_users?.updated_at,
    s.created_at
  ].filter(d => d && !isNaN(new Date(d).getTime())).map(d => new Date(d));

  if (dates.length > 0) {
    return new Date(Math.max(...dates.map(d => d.getTime()))).toISOString();
  }
  return s.created_at || new Date().toISOString();
};

const ViewUserActivityModal = ({
  open,
  onClose,
  contentType, // 'prayer', 'devotion', 'special_prayer'
  contentId,
  contentTitle,
  item
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    uniqueUsers: 0,
    confirmed_warrior: 0,
    unsubscribed: 0,
    requested: 0,
    commented: 0,
    opened: 0,
    reacted: 0,
    prayed: 0,
    subscribed: 0
  });
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !contentId) return;

    const fetchActivity = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const rawApiUrl = process.env.REACT_APP_API_URL || 'https://gpn.glocalprayer.net/admin-services/api/admin/';
        const cleanBaseUrl = rawApiUrl.replace(/\/+$/, '') + '/';

        let loadedActivities = [];
        let loadedCounts = null;

        // 1. Try centralized content-user-activity endpoint first
        try {
          const res = await fetch(`${cleanBaseUrl}content-user-activity`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              contentType,
              contentId,
              contentTitle,
              actionType: actionFilter,
              search,
              page: 1,
              perPage: 100
            })
          });

          if (res.ok) {
            const data = await res.json();
            if (data.success && Array.isArray(data.data) && data.data.length > 0) {
              loadedActivities = data.data.map(act => ({
                ...act,
                details: (act.details || '').replace(/\s*\(\s*undefined\s*-\s*undefined\s*\)/gi, '')
              }));
              if (data.counts) loadedCounts = data.counts;
            }
          }
        } catch (apiErr) {
          console.warn('content-user-activity endpoint not reachable, loading real database records:', apiErr.message);
        }

        // 2. Content-specific real database retrieval if centralized endpoint not available
        if (loadedActivities.length === 0) {
          if (contentType === 'special_prayer') {
            // Real Prayer Closet (Special Prayer) Subscribers
            try {
              // First attempt: direct filter by prayerId with broad org scope
              let subs = [];
              const subRes = await fetch(`${cleanBaseUrl}prayer-subscriber-list`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                  prayerId: contentId,
                  prayer_id: contentId,
                  organization: 0,
                  page: 1,
                  pageSize: 500
                })
              });

              if (subRes.ok) {
                const subData = await subRes.json();
                subs = subData.data || [];
              }

              // Fallback: If no subs returned with explicit prayerId filter, fetch general page and match locally
              if (subs.length === 0) {
                try {
                  const fallbackRes = await fetch(`${cleanBaseUrl}prayer-subscriber-list`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ organization: 0, page: 1, pageSize: 500 })
                  });
                  if (fallbackRes.ok) {
                    const fallbackData = await fallbackRes.json();
                    subs = fallbackData.data || [];
                  }
                } catch (fbErr) {
                  console.warn('Fallback subscriber list fetch error:', fbErr);
                }
              }

              // Only include REAL subscribers committed to THIS specific prayer closet
              const matchedSubs = subs.filter(s =>
                String(s.prayerId) === String(contentId) ||
                (s.session_prayers && String(s.session_prayers.id) === String(contentId)) ||
                (contentTitle && s.session_prayers?.title && s.session_prayers.title.toLowerCase().trim() === String(contentTitle).toLowerCase().trim())
              );

              // Deduplicate by subscriber id
              const seenSubIds = new Set();
              matchedSubs.forEach(s => {
                if (seenSubIds.has(s.id)) return;
                seenSubIds.add(s.id);

                const isUnsub = Boolean(s.deleted_at) || s.sub_conf === 'no' || s.status === 'Unsubscribed' || s.status === 'Declined' || s.status === 'inactive';
                const isConfirmed = !isUnsub && s.sub_conf === 'yes';
                const slotStr = formatTimeSlotDisplay(s.time_slots, s);
                const lastActiveTime = getSubscriberLastActivity(s);
                const slotDetail = slotStr ? ` (${slotStr})` : '';

                let statusText = 'Pending Confirmation';
                let actionType = 'subscribed';
                let actionLabel = 'Subscribed to Prayer Campaign';

                if (isUnsub) {
                  statusText = 'Unsubscribed';
                  actionType = 'unsubscribed';
                  actionLabel = 'Unsubscribed';
                } else if (isConfirmed) {
                  statusText = 'Confirmed Warrior';
                  actionType = 'confirmed_warrior';
                  actionLabel = 'Confirmed Prayer Warrior';
                }

                loadedActivities.push({
                  id: `sub_${s.id}`,
                  userId: s.userId || s.app_users?.id,
                  userName: `${s.app_users?.first_name || ''} ${s.app_users?.last_name || ''}`.trim() || s.app_users?.email || 'Active Subscriber',
                  userEmail: s.app_users?.email || '',
                  userAvatar: s.app_users?.profile_pic || null,
                  actionType,
                  actionLabel,
                  contentType: 'special_prayer',
                  contentId: contentId,
                  contentTitle: contentTitle || s.session_prayers?.title || 'Prayer Campaign',
                  createdAt: lastActiveTime,
                  registeredAt: s.created_at || null,
                  userLastUsed: s.app_users?.last_used || null,
                  details: isUnsub
                    ? `Previously subscribed${slotDetail} • Unsubscribed`
                    : `Committed to prayer schedule${slotDetail} • ${statusText}`
                });
              });

              // Check chain-prayer-monitoring if applicable for chain prayer slots
              if (item?.prayer_type === 'chain' || item?.pray_type === 'chain' || item?.flow_type === 'chain') {
                try {
                  const chainRes = await fetch(`${cleanBaseUrl}chain-prayer-monitoring/${contentId}`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                  }).catch(() => null);
                  if (chainRes && chainRes.ok) {
                    const chainData = await chainRes.json();
                    const slots = chainData?.data?.slots || [];
                    slots.forEach(slot => {
                      if (slot.booked_by && slot.booked_by.user_id) {
                        const exists = loadedActivities.some(a => String(a.userId) === String(slot.booked_by.user_id));
                        if (!exists) {
                          loadedActivities.push({
                            id: `chain_slot_${slot.id}`,
                            userId: slot.booked_by.user_id,
                            userName: slot.booked_by.name || 'Prayer Warrior',
                            userEmail: slot.booked_by.email || '',
                            userAvatar: null,
                            actionType: 'confirmed_warrior',
                            actionLabel: 'Chain Prayer Warrior',
                            contentType: 'special_prayer',
                            contentId: contentId,
                            contentTitle: contentTitle || 'Prayer Closet Campaign',
                            createdAt: slot.booked_by.booked_at || new Date().toISOString(),
                            details: `Booked slot #${slot.slot_number} (${slot.from_slot} - ${slot.to_slot}) • Confirmed`
                          });
                        }
                      }
                    });
                  }
                } catch (chainErr) {
                  console.warn('Chain prayer slot fetch:', chainErr);
                }
              }
            } catch (subErr) {
              console.warn('Subscriber list retrieval failed:', subErr.message);
            }

            // Also check for real open prayer comments
            try {
              const commRes = await fetch(`${cleanBaseUrl}prayer-comments-list`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ prayer_id: contentId, page: 1, pageSize: 30 })
              }).catch(() => null);

              if (commRes && commRes.ok) {
                const commData = await commRes.json().catch(() => null);
                const comments = commData?.data || [];
                comments.forEach((c) => {
                  const u = c.app_users || {};
                  const uName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Fellow Believer';
                  loadedActivities.push({
                    id: `comm_${c.id}`,
                    userId: c.user_id || u.id,
                    userName: uName,
                    userEmail: u.email || '',
                    userAvatar: u.profile_pic || null,
                    actionType: 'commented',
                    actionLabel: 'Encouraging Comment',
                    contentType: 'special_prayer',
                    contentId: contentId,
                    contentTitle: contentTitle || 'Prayer Closet Campaign',
                    createdAt: c.created_at || new Date().toISOString(),
                    details: c.comment || 'Shared words of encouragement and agreement in prayer'
                  });
                });
              }
            } catch (cErr) {
              console.warn('Comments retrieval failed:', cErr);
            }

            // Sort activities by most recent activity time descending
            loadedActivities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          } else if (contentType === 'prayer') {
            // Real Prayer Requests Activity Tracking
            // 1. Add REAL Requester (the actual user who authored and submitted this prayer petition)
            const reqUser = item?.app_users || {};
            const reqFullName = [reqUser.first_name, reqUser.last_name].filter(Boolean).join(' ').trim();
            const displayName = reqFullName || reqUser.email || '';

            if (displayName || item?.created_at) {
              loadedActivities.push({
                id: `pray_req_${contentId}`,
                userId: reqUser.id || item?.user_id,
                userName: displayName || 'Community Believer',
                userEmail: reqUser.email || '',
                userAvatar: reqUser.profile_pic || null,
                actionType: 'requested',
                actionLabel: 'Requested Prayer',
                contentType: 'prayer',
                contentId: contentId,
                contentTitle: contentTitle || item?.title || 'Prayer Request',
                createdAt: item?.created_at || new Date().toISOString(),
                details: item?.description
                  ? (item.description.length > 110 ? item.description.substring(0, 110) + '...' : item.description)
                  : 'Submitted prayer request for community intercession'
              });
            }

            // 2. Fetch REAL comments for this prayer
            try {
              const prayersBaseUrl = rawApiUrl.includes('glocalprayer.net')
                ? 'https://gpn.glocalprayer.net/prayers/api/prayer/'
                : `${cleanBaseUrl}`;
              
              const commFormData = new FormData();
              commFormData.append('prayer_id', contentId);

              const commRes = await fetch(`${prayersBaseUrl}commentlist`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: commFormData
              }).catch(() => null);

              if (commRes && commRes.ok) {
                const commData = await commRes.json().catch(() => null);
                const comments = commData?.comments || commData?.data || [];
                if (Array.isArray(comments)) {
                  comments.forEach(c => {
                    const u = c.app_users || {};
                    const cName = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || 'Community Believer';
                    loadedActivities.push({
                      id: `comm_${c.id}`,
                      userId: u.id || c.user_id,
                      userName: cName,
                      userEmail: u.email || '',
                      userAvatar: u.profile_pic || null,
                      actionType: 'commented',
                      actionLabel: 'Commented',
                      contentType: 'prayer',
                      contentId: contentId,
                      contentTitle: contentTitle || 'Prayer Request',
                      createdAt: c.created_at || new Date().toISOString(),
                      details: c.comment || 'Shared encouragement in prayer agreement'
                    });
                  });
                }
              }
            } catch (commErr) {
              console.warn('Real prayer comments fetch failed:', commErr);
            }

            // 3. Fetch REAL reactions for this prayer
            try {
              const prayersBaseUrl = rawApiUrl.includes('glocalprayer.net')
                ? 'https://gpn.glocalprayer.net/prayers/api/prayer/'
                : `${cleanBaseUrl}`;

              const rxFormData = new FormData();
              rxFormData.append('prayer_id', contentId);

              const rxRes = await fetch(`${prayersBaseUrl}reactionsdetails`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: rxFormData
              }).catch(() => null);

              if (rxRes && rxRes.ok) {
                const rxData = await rxRes.json().catch(() => null);
                const reactions = rxData?.reactionsdata || [];
                if (Array.isArray(reactions)) {
                  reactions.forEach((r, rIdx) => {
                    const userNames = (r.users || '').split(',').filter(Boolean);
                    userNames.forEach((uName, uIdx) => {
                      const rxType = String(r.reaction_type || '').toLowerCase();
                      const isAmen = rxType.includes('amen');
                      loadedActivities.push({
                        id: `rx_${contentId}_${rIdx}_${uIdx}`,
                        userId: null,
                        userName: uName.trim(),
                        userEmail: '',
                        userAvatar: null,
                        actionType: isAmen ? 'reacted' : 'prayed',
                        actionLabel: isAmen ? `Reacted (${r.reaction_type})` : `Prayed (${r.reaction_type})`,
                        contentType: 'prayer',
                        contentId: contentId,
                        contentTitle: contentTitle || 'Prayer Request',
                        createdAt: r.created_at || new Date().toISOString(),
                        details: `Interceded in prayer agreement (${r.reaction_type})`
                      });
                    });
                  });
                }
              }
            } catch (rxErr) {
              console.warn('Real prayer reactions fetch failed:', rxErr);
            }

            // Sort activities chronologically descending
            loadedActivities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          } else if (contentType === 'devotion') {
            // Real Devotion interactions
            loadedActivities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          }
        }

        // Calculate counts
        if (loadedActivities.length > 0) {
          setActivities(loadedActivities);
          setCounts(loadedCounts || {
            total: loadedActivities.length,
            uniqueUsers: new Set(loadedActivities.map(a => a.userId).filter(Boolean)).size || loadedActivities.length,
            confirmed_warrior: loadedActivities.filter(a => a.actionType === 'confirmed_warrior').length,
            unsubscribed: loadedActivities.filter(a => a.actionType === 'unsubscribed').length,
            requested: loadedActivities.filter(a => a.actionType === 'requested').length,
            commented: loadedActivities.filter(a => a.actionType === 'commented').length,
            opened: loadedActivities.filter(a => a.actionType === 'opened').length,
            reacted: loadedActivities.filter(a => a.actionType === 'reacted').length,
            prayed: loadedActivities.filter(a => a.actionType === 'prayed').length,
            subscribed: loadedActivities.filter(a => a.actionType === 'subscribed').length,
          });
        } else {
          setActivities([]);
          setCounts({
            total: 0,
            uniqueUsers: 0,
            confirmed_warrior: 0,
            unsubscribed: 0,
            requested: 0,
            commented: 0,
            opened: 0,
            reacted: 0,
            prayed: 0,
            subscribed: 0
          });
        }
      } catch (err) {
        console.error('Error fetching content user activity:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [open, contentId, contentType, contentTitle, item, actionFilter, search]);

  const filteredActivities = activities.filter(a => {
    if (actionFilter !== 'all' && a.actionType !== actionFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        (a.userName && a.userName.toLowerCase().includes(q)) ||
        (a.userEmail && a.userEmail.toLowerCase().includes(q))
      );
    }
    return true;
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isLight ? '#FFFFFF' : 'linear-gradient(145deg, #15222E 0%, #1A2E3B 100%)',
          color: isLight ? '#0F172A' : '#FFFFFF',
          border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(76, 161, 175, 0.35)',
          boxShadow: isLight ? '0 12px 32px rgba(0,0,0,0.1)' : '0 20px 50px rgba(0,0,0,0.5)',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5, borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <TimelineIcon sx={{ color: '#177373', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              User Activity Tracking
            </Typography>
            <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.6)' }}>
              {contentTitle ? `"${contentTitle}"` : `Item #${contentId}`} • Real-time user engagements
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2.5 }}>
        {/* KPI Chips */}
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Chip
            label={`Total: ${counts.total || activities.length}`}
            onClick={() => setActionFilter('all')}
            color={actionFilter === 'all' ? 'primary' : 'default'}
            variant={actionFilter === 'all' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          />
          {counts.confirmed_warrior > 0 && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              label={`Confirmed Warriors: ${counts.confirmed_warrior}`}
              onClick={() => setActionFilter('confirmed_warrior')}
              color={actionFilter === 'confirmed_warrior' ? 'success' : 'default'}
              variant={actionFilter === 'confirmed_warrior' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
          {counts.subscribed > 0 && (
            <Chip
              icon={<NotificationsActiveIcon sx={{ fontSize: 16 }} />}
              label={`Subscribed: ${counts.subscribed}`}
              onClick={() => setActionFilter('subscribed')}
              color={actionFilter === 'subscribed' ? 'warning' : 'default'}
              variant={actionFilter === 'subscribed' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
          {counts.unsubscribed > 0 && (
            <Chip
              icon={<CloseIcon sx={{ fontSize: 16 }} />}
              label={`Unsubscribed: ${counts.unsubscribed}`}
              onClick={() => setActionFilter('unsubscribed')}
              color={actionFilter === 'unsubscribed' ? 'error' : 'default'}
              variant={actionFilter === 'unsubscribed' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
          {counts.requested > 0 && (
            <Chip
              icon={<CampaignIcon sx={{ fontSize: 16 }} />}
              label={`Requested: ${counts.requested}`}
              onClick={() => setActionFilter('requested')}
              color={actionFilter === 'requested' ? 'secondary' : 'default'}
              variant={actionFilter === 'requested' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
          {counts.prayed > 0 && (
            <Chip
              icon={<VolunteerActivismIcon sx={{ fontSize: 16 }} />}
              label={`Prayed: ${counts.prayed}`}
              onClick={() => setActionFilter('prayed')}
              color={actionFilter === 'prayed' ? 'success' : 'default'}
              variant={actionFilter === 'prayed' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
          {counts.reacted > 0 && (
            <Chip
              icon={<FavoriteIcon sx={{ fontSize: 16 }} />}
              label={`Reacted: ${counts.reacted}`}
              onClick={() => setActionFilter('reacted')}
              color={actionFilter === 'reacted' ? 'secondary' : 'default'}
              variant={actionFilter === 'reacted' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
          {counts.commented > 0 && (
            <Chip
              icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
              label={`Comments: ${counts.commented}`}
              onClick={() => setActionFilter('commented')}
              color={actionFilter === 'commented' ? 'info' : 'default'}
              variant={actionFilter === 'commented' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
          {counts.opened > 0 && (
            <Chip
              icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
              label={`Opened: ${counts.opened}`}
              onClick={() => setActionFilter('opened')}
              color={actionFilter === 'opened' ? 'info' : 'default'}
              variant={actionFilter === 'opened' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            />
          )}
        </Box>

        {/* Search Input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search by user name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', fontSize: 20 }} />
              </InputAdornment>
            )
          }}
          sx={{
            mb: 2,
            bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)',
            borderRadius: 2
          }}
        />

        {/* User Activities Table */}
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 400,
            bgcolor: isLight ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
            border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 2
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: isLight ? '#F1F5F9' : '#14222B' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: isLight ? '#F1F5F9' : '#14222B' }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: isLight ? '#F1F5F9' : '#14222B' }}>User Last Activity (Date & Time)</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: isLight ? '#F1F5F9' : '#14222B' }}>Activity Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={30} sx={{ color: '#177373' }} />
                  </TableCell>
                </TableRow>
              ) : filteredActivities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)' }}>
                      No user activity recorded for this criteria yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredActivities.map((act) => {
                  const badge = getActionBadge(act.actionType);
                  return (
                    <TableRow key={act.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.2}>
                          <Avatar
                            src={act.userAvatar}
                            sx={{ width: 32, height: 32, bgcolor: '#177373', fontSize: 13, fontWeight: 700 }}
                          >
                            {act.userName ? act.userName.charAt(0) : 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                              {act.userName || 'Anonymous Believer'}
                            </Typography>
                            {act.userEmail ? (
                              <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', display: 'block' }}>
                                {act.userEmail}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={badge.icon}
                          label={badge.label}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            '& .MuiChip-icon': { color: badge.color }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.6}>
                            <CalendarTodayIcon sx={{ fontSize: 13, color: isLight ? '#0D9488' : '#4CA1AF' }} />
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: isLight ? '#0F172A' : '#FFFFFF' }}>
                              {act.createdAt ? new Date(act.createdAt).toLocaleDateString() : '—'}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.6} mt={0.3}>
                            <AccessTimeIcon sx={{ fontSize: 13, color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)' }} />
                            <Typography variant="caption" fontWeight={600} sx={{ color: isLight ? '#0D9488' : '#64d2e2' }}>
                              {act.createdAt ? new Date(act.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
                            </Typography>
                          </Box>
                          {act.userLastUsed && (
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.68rem', color: isLight ? '#64748B' : 'rgba(255,255,255,0.45)', mt: 0.3 }}>
                              App login: {new Date(act.userLastUsed).toLocaleDateString()} {new Date(act.userLastUsed).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: isLight ? '#475569' : 'rgba(255,255,255,0.7)' }}>
                          {act.details || 'Standard user engagement'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: '#177373',
            '&:hover': { bgcolor: '#125c5c' }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewUserActivityModal;
